import React, { useState, useEffect, useMemo } from 'react';
import { hrEmployeesApi, projectsTeamApi, type EmployeeDto } from '../../services/api';

export interface ProjectTeamMemberDto {
  id: string;
  project_id: string;
  employee_id: string;
  employee_name: string;
  department: string;
  job_title: string;
  hourly_rate: number;
}

const HOURS_PER_MONTH = 176;

function computeHourlyRate(emp: EmployeeDto): number {
  const rate = emp.pay_rate ?? 0;
  if (rate <= 0) return 0;
  const salaryType = (emp.salary_type || '').toLowerCase();
  if (salaryType.includes('hourly') || salaryType.includes('per hour')) {
    return rate;
  }
  return Math.round((rate / HOURS_PER_MONTH) * 100) / 100;
}

function formatPeso(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

interface AddTeamMemberModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (member: ProjectTeamMemberDto) => void;
  projectId: string;
  existingMembers: ProjectTeamMemberDto[];
}

export default function AddTeamMemberModal({
  open,
  onClose,
  onSuccess,
  projectId,
  existingMembers,
}: AddTeamMemberModalProps) {
  const [employees, setEmployees] = useState<EmployeeDto[]>([]);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDto | null>(null);
  const [hourlyOverride, setHourlyOverride] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      hrEmployeesApi
        .list()
        .then((res) => {
          if (res.data) setEmployees(res.data);
          else setEmployees([]);
        })
        .catch(() => setEmployees([]))
        .finally(() => setLoading(false));
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDepartmentFilter('');
      setSelectedEmployee(null);
      setHourlyOverride('');
      setError(null);
    }
  }, [open]);

  const existingIds = new Set(existingMembers.map((m) => m.employee_id));
  const availableEmployees = employees.filter((e) => !existingIds.has(e.id));

  const departments = useMemo(() => {
    const depts = new Set<string>();
    availableEmployees.forEach((e) => {
      const d = (e.department || '').trim();
      if (d) depts.add(d);
    });
    return Array.from(depts).sort();
  }, [availableEmployees]);

  const filteredEmployees = useMemo(() => {
    let list = availableEmployees;
    if (departmentFilter) {
      list = list.filter((e) => (e.department || '').trim() === departmentFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((e) => {
        const name = (e.full_name || `${e.first_name} ${e.last_name}`.trim()).toLowerCase();
        const email = (e.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      });
    }
    return list.sort((a, b) => {
      const na = a.full_name || `${a.first_name} ${a.last_name}`;
      const nb = b.full_name || `${b.first_name} ${b.last_name}`;
      return na.localeCompare(nb);
    });
  }, [availableEmployees, departmentFilter, search]);

  const computedHourly = selectedEmployee ? computeHourlyRate(selectedEmployee) : 0;
  const displayHourlyRate = selectedEmployee
    ? (hourlyOverride !== '' && Number(hourlyOverride) >= 0 ? Number(hourlyOverride) : computedHourly)
    : 0;

  const handleSelectEmployee = (emp: EmployeeDto) => {
    setSelectedEmployee(emp);
    setHourlyOverride(computeHourlyRate(emp));
  };

  const handleAdd = async () => {
    if (!selectedEmployee) {
      setError('Please select an employee from the list.');
      return;
    }
    const rate = displayHourlyRate;
    if (rate <= 0) {
      setError('Hourly rate must be greater than 0.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await projectsTeamApi.add(projectId, {
        employee_id: selectedEmployee.id,
        hourly_rate: rate,
      });
      if (res.data) {
        onSuccess(res.data);
        onClose();
      } else if (res.message) {
        setError(res.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add team member');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedEmployee(null);
    setHourlyOverride('');
    setError(null);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} aria-hidden="true" />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        <div className="shrink-0 px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Add Team Member</h2>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by employee name..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
              />
            </div>
            <div className="w-full sm:w-48">
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22%20%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat pr-10"
              >
                <option value="">All Departments</option>
                {departments.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {error && (
            <div className="shrink-0 mx-6 mt-4 rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-500 text-sm">Loading employees...</div>
            ) : filteredEmployees.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
                <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="font-medium">No employees found</p>
                <p className="text-sm mt-1">Try adjusting your search or department filter.</p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Employee Name
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Department
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Monthly (₱)
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Hourly (₱)
                      </th>
                      <th className="w-16 py-3 px-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEmployees.map((emp) => {
                      const name = emp.full_name || `${emp.first_name} ${emp.last_name}`.trim();
                      const hourly = computeHourlyRate(emp);
                      const isSelected = selectedEmployee?.id === emp.id;
                      return (
                        <tr
                          key={emp.id}
                          className={`transition-colors ${
                            isSelected ? 'bg-brand-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="py-3 px-4 font-medium text-gray-900">{name}</td>
                          <td className="py-3 px-4 text-gray-600">{emp.department || '—'}</td>
                          <td className="py-3 px-4 text-right text-gray-900">{formatPeso(emp.pay_rate || 0)}</td>
                          <td className="py-3 px-4 text-right font-medium text-brand-600">{formatPeso(hourly)}/hr</td>
                          <td className="py-3 px-2">
                            <button
                              type="button"
                              onClick={() => handleSelectEmployee(emp)}
                              className={`w-full py-2 px-3 rounded-lg text-xs font-semibold transition-colors ${
                                isSelected
                                  ? 'bg-brand-600 text-white hover:bg-brand-700'
                                  : 'bg-gray-100 text-gray-700 hover:bg-brand-100 hover:text-brand-700'
                              }`}
                            >
                              {isSelected ? 'Selected' : 'Select'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {selectedEmployee && (
            <div className="shrink-0 border-t border-gray-100 bg-gray-50 px-6 py-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate (₱) *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₱</span>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={hourlyOverride === '' ? '' : hourlyOverride}
                      onChange={(e) =>
                        setHourlyOverride(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)
                      }
                      placeholder={`Computed: ${computedHourly.toFixed(2)}`}
                      className="w-full rounded-xl border border-gray-200 bg-white pl-8 pr-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    From monthly ÷ {HOURS_PER_MONTH} hrs. Edit to override.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {saving ? 'Adding...' : `Add ${selectedEmployee.full_name || `${selectedEmployee.first_name} ${selectedEmployee.last_name}`.trim()} to Team`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
